import { initializeDB } from '../config/db';
import { JSSI_SCHEMA_STORE } from '../config/constants';
import axiosInstance from '../axiosInstance';

/**
 * Access to the Job Site Safety Inspection question schema.
 *
 * The questions are authored in the admin and published as numbered revisions; the device caches
 * every revision it has seen, keyed by version. Two things depend on that:
 *   - the form renders the CURRENT published revision for a new inspection;
 *   - reopening a draft renders the revision that draft was started on, so a question cannot change
 *     wording, scoring or visibility underneath a half-finished report.
 *
 * Reads never hit the network — the form reads IndexedDB, and only the sync pass talks to the API.
 */

const CURRENT_VERSION_KEY = 'jssiSchemaCurrentVersion';
const ENDPOINT = '/job-site-safety-inspection-schema';

// How many past revisions to keep on the device. Enough to cover any draft still lying around,
// small enough that the store cannot grow without bound.
const KEEP_REVISIONS = 10;

const readCurrentVersion = () => {
    const raw = localStorage.getItem(CURRENT_VERSION_KEY);
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
};

const isUsableSchema = (schema) =>
    !!schema && Array.isArray(schema.sections) && schema.sections.length > 0;

const jobSiteSafetyInspectionSchemaService = {
    CURRENT_VERSION_KEY,

    /** Version of the published revision this device last saw, or null before the first sync. */
    getCurrentVersion: readCurrentVersion,

    async putSchema(schema) {
        if (!isUsableSchema(schema) || typeof schema.version !== 'number') return null;

        const db = await initializeDB();
        const tx = db.transaction([JSSI_SCHEMA_STORE], 'readwrite');
        await tx.objectStore(JSSI_SCHEMA_STORE).put(schema);
        await tx.done;
        return schema;
    },

    async getSchemaByVersion(version) {
        if (typeof version !== 'number') return null;

        const db = await initializeDB();
        const tx = db.transaction([JSSI_SCHEMA_STORE], 'readonly');
        const schema = await tx.objectStore(JSSI_SCHEMA_STORE).get(version);
        return isUsableSchema(schema) ? schema : null;
    },

    /** Newest cached revision — the last resort when a specific one cannot be produced. */
    async getNewestCachedSchema() {
        const db = await initializeDB();
        const tx = db.transaction([JSSI_SCHEMA_STORE], 'readonly');
        const all = await tx.objectStore(JSSI_SCHEMA_STORE).getAll();
        const usable = (all || []).filter(isUsableSchema);
        if (usable.length === 0) return null;

        return usable.sort((a, b) => b.version - a.version)[0];
    },

    /** The revision a NEW inspection should be filled out against. */
    async getActiveSchema() {
        const current = readCurrentVersion();
        if (current !== null) {
            const exact = await this.getSchemaByVersion(current);
            if (exact) return exact;
        }

        return this.getNewestCachedSchema();
    },

    /**
     * The revision a given draft was started on.
     *
     * If it is not cached — the device was wiped, or the draft was carried over from another install —
     * fetch that exact revision. Only when that fails too does it fall back to whatever is cached, and
     * the caller is told, because a draft rendered against a different revision may show different
     * questions than the inspector started with.
     *
     * @returns {Promise<{schema: object|null, exact: boolean}>}
     */
    async getSchemaForDraft(version) {
        if (typeof version !== 'number') {
            return { schema: await this.getActiveSchema(), exact: true };
        }

        const cached = await this.getSchemaByVersion(version);
        if (cached) return { schema: cached, exact: true };

        try {
            const response = await axiosInstance.get(`${ENDPOINT}?version=${encodeURIComponent(version)}`);
            const body = response.data || {};
            if (isUsableSchema(body.data)) {
                await this.putSchema(body.data);
                return { schema: body.data, exact: body.exact !== false && body.data.version === version };
            }
        } catch (error) {
            console.warn(`[JSSI schema] Could not fetch revision ${version}:`, error.message);
        }

        const fallback = await this.getActiveSchema();
        return { schema: fallback, exact: false };
    },

    /**
     * Sync handler wired into syncConfig.
     *
     * Modelled on featureVisibility: a failed or empty refresh leaves the cache exactly as it was,
     * because an inspector on a job site is far better off with slightly stale questions than with
     * none at all.
     */
    async sync(db, storeName) {
        const knownVersion = readCurrentVersion();
        const query = knownVersion !== null ? `?knownVersion=${knownVersion}` : '';

        const response = await axiosInstance.get(`${ENDPOINT}${query}`);
        const body = response.data || {};

        if (body.noChanges) {
            console.log(`[JSSI schema] Already on revision ${body.version}`);
            return;
        }

        // Nothing published on the server yet. Keep whatever the device already has.
        if (body.bootstrap || !isUsableSchema(body.data)) {
            console.log('[JSSI schema] No published revision on the server; keeping the cached one');
            return;
        }

        const schema = body.data;
        const tx = db.transaction([storeName], 'readwrite');
        const store = tx.objectStore(storeName);
        await store.put(schema);

        // Drop revisions old enough that no draft could still be pointing at them.
        const all = await store.getAll();
        const stale = (all || [])
            .map((s) => s.version)
            .filter((v) => typeof v === 'number')
            .sort((a, b) => b - a)
            .slice(KEEP_REVISIONS);
        for (const version of stale) {
            await store.delete(version);
        }

        await tx.done;

        localStorage.setItem(CURRENT_VERSION_KEY, String(schema.version));
        console.log(
            `[JSSI schema] Synced revision ${schema.version} (${schema.sections.length} sections, hash ${schema.hash})`
        );
    },
};

export default jobSiteSafetyInspectionSchemaService;
